import { TVisitorLocation } from "@/types";
import { base64Decode, base64Encode } from "./utils";

type TPostcodeDiscountMetafield = {
  value?: string | null;
} | null;

// Records the pricing tier applied to a line. The reduced discount in
// _visitorLocationInfo cannot be reversed into a tier, so it is stored separately.
export const POSTCODE_DISCOUNT_TIER_ATTRIBUTE = "_postcodeDiscountTier";

/**
 * Reads hk-app.postcode-discount-adjustment (decimal stored as a string). The session
 * editor writes the tier as a negative amount ("-5.0" for the -5 PO tier), so the
 * magnitude is how much the postcode discount is reduced by. Returns null when the
 * metafield is missing, cleared, zero, or not a number so standard visitor postcode
 * discount logic can apply.
 */
export function parsePostcodeDiscountAdjustment(metafield?: TPostcodeDiscountMetafield): number | null {
  if (!metafield?.value) {
    return null;
  }

  const parsed = Number(metafield.value);
  if (!Number.isFinite(parsed) || parsed === 0) {
    return null;
  }

  return Math.abs(parsed);
}

/**
 * Postcode discount to apply for a variant:
 * - No visitor eligibility → $0
 * - Variant metafield set ($59 -5 PO / -10 PO tiers) → the visitor's discount
 *   reduced by that amount and floored at $0; the listed price stays $59
 * - Otherwise → the standard location-based discount
 */
export function getEffectivePostcodeDiscount(visitorDiscountAmount: number, metafield?: TPostcodeDiscountMetafield): number {
  const visitorDiscount = typeof visitorDiscountAmount === "number" && Number.isFinite(visitorDiscountAmount) ? visitorDiscountAmount : 0;

  if (visitorDiscount <= 0) {
    return 0;
  }

  const reduction = parsePostcodeDiscountAdjustment(metafield);
  return reduction === null ? visitorDiscount : Math.max(0, visitorDiscount - reduction);
}

// Reads a line attribute from either cart shape: Storefront API lines (`attributes`)
// or theme /cart.js items (`properties`, as an array or an object).
export function readCartLineAttribute(cartLine: any, key: string): string | null {
  if (!cartLine) {
    return null;
  }

  if (Array.isArray(cartLine.attributes)) {
    return cartLine.attributes.find((attribute: any) => attribute?.key === key)?.value ?? null;
  }

  const properties = cartLine.properties;
  if (Array.isArray(properties)) {
    return properties.find((property: any) => property?.name === key)?.value ?? null;
  }
  if (properties && typeof properties === "object") {
    return properties[key] ?? null;
  }

  return null;
}

/**
 * Encoded tier to write to POSTCODE_DISCOUNT_TIER_ATTRIBUTE, or null for untagged
 * variants. Tagged lines get no _visitorLocationInfo, so this attribute is the only
 * record that the line was already postcode-adjusted.
 */
export function getPostcodeDiscountTierForCart(metafield?: TPostcodeDiscountMetafield): string | null {
  const tier = parsePostcodeDiscountAdjustment(metafield);
  return tier === null ? null : base64Encode(String(tier));
}

/**
 * Recovers a cart line's pricing tier from the attribute written at add-to-cart time.
 * Cart JSON carries no metafields, so lines rebuilt from the cart (page reload, or
 * tracks whose variants are not refetched) would otherwise take the standard discount
 * and display a price below what the checkout charges.
 */
export function getPostcodeDiscountAdjustmentFromCartLine(cartLine: any): { value: string } | null {
  const encoded = readCartLineAttribute(cartLine, POSTCODE_DISCOUNT_TIER_ATTRIBUTE);
  if (!encoded) {
    return null;
  }

  try {
    const tier = Number(base64Decode(encoded));

    if (!Number.isFinite(tier) || tier <= 0) {
      return null;
    }

    return { value: String(tier) };
  } catch {
    return null;
  }
}

export function getVisitorLocationInfoForCart(visitorLocation: TVisitorLocation | null | undefined, metafield?: TPostcodeDiscountMetafield): TVisitorLocation | null {
  if (!visitorLocation || visitorLocation.distance <= 0 || visitorLocation.discountAmount <= 0) {
    return null;
  }

  const discountAmount = getEffectivePostcodeDiscount(visitorLocation.discountAmount, metafield);
  if (discountAmount <= 0) {
    return null;
  }

  return {
    ...visitorLocation,
    discountAmount,
  };
}
